import { ROOT_URL } from 'gatsby-env-variables'
import Button from 'components/Button'
import ROUTES from 'Routes'
import { handlePerformanceMark } from 'utils/trackings/newRelicEvents'
import { navigate } from 'gatsby'
import router from 'utils/router'

import './LandingTestimonials.scss'

const TEXT_PATTERN = '(\\w|\\s|[áâãéêíóôõúçñÁÀÂÃÉÍÓÔÕÚÇ$-?!%])+'
const EMPHASIS_REGEX = new RegExp(`({{${TEXT_PATTERN}}})`, 'gi')
const BOLDER_REGEX = new RegExp(`(\\[\\[${TEXT_PATTERN}\\]\\])`, 'gi')

const handleBoldAndAriaText = (text) => {
  if (!text) return <></>

  let textToRender = text

  const isBolder = (text) => text?.match(BOLDER_REGEX)
  const splittedTextWithBolder = text.split(BOLDER_REGEX)

  const filteredBolderText = splittedTextWithBolder
    .map((text, index) => {
      if (index === 0) return text
      if (isBolder(splittedTextWithBolder[index - 1])) return
      return text
    })
    .filter((text) => text)

  const renderBolderText = (text, index) => {
    const bolderText = text.replace(/(\[\[|\]\])/gi, '')

    return <strong key={`bolderText${index}`}>{bolderText}</strong>
  }

  const renderSmileText = (text) => {
    const textWithoutSmile = text.replace(/(;|\))/gi, '')

    return (
      <>
        {textWithoutSmile}
        <span aria-hidden="true">;)</span>
      </>
    )
  }

  textToRender = filteredBolderText.map((text, index) => {
    if (isBolder(text)) {
      return renderBolderText(text, index)
    }

    if (text.includes(';)')) {
      return renderSmileText(text)
    }

    return text
  })

  return <span>{textToRender}</span>
}

const renderTextWithEmphasis = (text, highlightColor) => {
  if (!text) return <></>

  let textToRender = text

  if (highlightColor) {
    const isEmphasis = (text) => text?.match(EMPHASIS_REGEX)

    const splittedTextWithEmphasis = text.split(EMPHASIS_REGEX)

    const filteredEmphasisText = splittedTextWithEmphasis
      .map((text, index) => {
        if (index === 0) return text
        if (isEmphasis(splittedTextWithEmphasis[index - 1])) return
        return text
      })
      .filter((text) => text)

    const renderEmphasisText = (text, index) => {
      const emphasisText = text.replace(/({{|}})/gi, '')

      return (
        <span style={{ color: highlightColor }} key={`emphasisText${index}`}>
          {emphasisText}
        </span>
      )
    }

    textToRender = filteredEmphasisText.map((text, index) => {
      if (isEmphasis(text)) {
        return renderEmphasisText(text, index)
      }

      return text
    })
  }

  return <>{textToRender}</>
}

const renderTestimonialDate = (date) => {
  if (!date) return <></>

  return new Date(date).toLocaleString().split(',')[0]
}

const LandingTestimonials = ({ blok, location }) => {
  if (!blok) return null

  return (
    <section
      className="landing-testimonials"
      style={{
        background: blok?.background_color,
      }}
    >
      <div className="landing-testimonials__container">
        <div className="landing-testimonials__header">
          <h2
            className="landing-testimonials__title"
            style={{
              color: blok?.text_color,
            }}
          >
            {renderTextWithEmphasis(blok?.title, blok?.highlight_color)}
          </h2>
          <p
            className="landing-testimonials__subtitle"
            style={{
              color: blok?.text_color,
            }}
          >
            {blok?.description}
          </p>
          <ol className="landing-testimonials__list-steps">
            {blok?.info_list?.map((item, index) => (
              <li
                key={index}
                className="landing-testimonials__list-steps-item"
                style={{
                  color: blok?.text_color,
                  '--list-steps-counter-color': blok?.highlight_color,
                }}
              >
                {handleBoldAndAriaText(item?.text)}
              </li>
            ))}
          </ol>

          <Button
            className="button"
            style={{
              backgroundColor: blok?.cta_background_color,
              color: blok?.cta_text_color,
            }}
            onClick={() => {
              handlePerformanceMark(ROUTES.register.pageName)
              navigate(ROUTES.register.path, {
                state: {
                  source_url: location.href,
                  mp_uuid: router.getQueryString('mp_uuid'),
                },
              })
            }}
            data-unique="a2ed7ee6-7b02-4d10-9117-23a4279e9743"
            data-make-location="lp"
            data-make-position="lp-testimonials-button"
            data-make-actionoutcome="check_cpf"
            data-make-element="button"
            data-make-outboundurl={`${ROOT_URL}${ROUTES.register.path}`}
          >
            {blok?.cta_text}
          </Button>
        </div>

        <div className="landing-testimonials__container-video">
          <iframe
            width="560"
            height="315"
            src={blok?.video_url}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>

        <ul className="landing-testimonials__list">
          {blok?.testimonials?.map((item, index) => (
            <li key={index} className="landing-testimonials__list-item">
              <span className="landing-testimonials__list-item-name">
                {item?.name}
              </span>
              <p className="landing-testimonials__list-item-testimonial">
                {item?.content}
              </p>
              <span className="landing-testimonials__list-item-date">
                {renderTestimonialDate(item?.date)}
              </span>
            </li>
          ))}
        </ul>

        <p
          className="landing-testimonials__support-text"
          style={{
            color: blok?.text_color,
          }}
        >
          {blok?.support_text}
        </p>

        <Button
          className="button"
          style={{
            backgroundColor: blok?.cta_background_color,
            color: blok?.cta_text_color,
          }}
          onClick={() => {
            handlePerformanceMark(ROUTES.register.pageName)
            navigate(ROUTES.register.path, {
              state: {
                source_url: location.href,
                mp_uuid: router.getQueryString('mp_uuid'),
              },
            })
          }}
          data-unique="d86b7bd4-09c2-4287-9bb4-89f95f9ea41e"
          data-make-location="lp"
          data-make-position="lp-testimonials-button"
          data-make-actionoutcome="check_cpf"
          data-make-element="button"
          data-make-outboundurl={`${ROOT_URL}${ROUTES.register.path}`}
        >
          {blok?.cta_text}
        </Button>
      </div>
    </section>
  )
}

export default LandingTestimonials
